import {
  Directive,
  ElementRef,
  ViewContainerRef,
  Input,
  Host,
  Optional,
  Self,
} from "@angular/core";
import { NativeDesktopSupport } from "./native-desktop-support";
import { BfxCalendarComponent } from "../order-entry/interface/controls/good-from-until/bfx-calendar.component";

import { OELoggerService } from "../order-entry/services/basic/oe-logger.service";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { DomHandlerMode, DOM_HANDLER_MODE_TOKEN, DomHandlerService } from "@bfx/primeng/dom";
import { NativeDesktopWindow } from "../../shared/native-desktop/NativeDesktopWindow";
import { NativeDesktop } from "shared/native-desktop/NativeDesktopSupportShared";
import { WindowType } from "shared/native-desktop/NativeDesktopWindowOptions";
import { MonitorInfo, Rect } from "shared/native-desktop/NativeDesktopData";

@Directive({
  selector: "[nativeDesktopCalendar]",
  providers: [
    {
      provide: DOM_HANDLER_MODE_TOKEN,
      useFactory: () => {
        if (NativeDesktopSupport.isNativeDesktopContainer())
          return new DomHandlerService(DomHandlerMode.NativeContainer);
        return new DomHandlerService(DomHandlerMode.Browser);
      },
    },
  ],
  standalone: false,
})
export class NativeDesktopCalendar {
  public static _calendarWindow: NativeDesktopWindow;
  public static _calendarDropdownOpen: boolean = false;
  public static _activeComponent: BfxCalendarComponent;
  private static _initWindowPromise: Promise<void>;

  private _activeWindow: boolean = false;
  private _groupName: string = null;
  private _screenBounds: Rect;
  private _originalBounds: any;

  @Input("nativeDesktopCalendar")
  public target: string;

  constructor(
    @Host() @Self() @Optional() component: BfxCalendarComponent,
    element: ElementRef,
    protected oeLogger: OELoggerService,
  ) {
    this.oeLogger = this.oeLogger.for(__dirname);
    this.initialize(component, element);
  }

  private initialize(component: BfxCalendarComponent, element: ElementRef): void {
    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      if (component instanceof BfxCalendarComponent) {
        let calendar: BfxCalendarComponent = component as BfxCalendarComponent;
        calendar.ngAfterContentInit = () => {};

        calendar.ngCalendarDecorator = () => {
          calendar.appendTo = null;
        };

        calendar.showOverlay = this.showOverlay.bind(this, calendar);
        calendar.onHideOverlay.subscribe(this.onHideOverlay.bind(this));
        calendar.appendTo = null;

        if (calendar.documentClickListener != null) {
          calendar.renderer.listen(
            element.nativeElement.ownerDocument.body,
            "click",
            calendar.documentClickListener,
          );
        }
      }

      let monitorInfo = NativeDesktopSupport.getMonitorInfo();
      this._screenBounds = monitorInfo.primaryMonitor.monitorRect;
      NativeDesktopCalendar.initWindow();
    }
  }

  public showOverlay(calendar: BfxCalendarComponent): void {
    calendar.afterViewInitReady.then(
      function (this: NativeDesktopCalendar) {
        requestAnimationFrame(
          (() => {
            NativeDesktopCalendar._activeComponent = calendar;

            let calendarOverlayElement: HTMLDivElement = calendar.overlayViewChild.nativeElement;
            NativeDesktopCalendar._calendarWindow.attachToParentElement(calendar.el.nativeElement);

            let element: Element = calendar.el.nativeElement;
            element.removeAttribute("appendTo");

            calendar.appendTo = null;
            calendar.overlayShown = calendar.overlayVisible = true;
            calendar.cd.detectChanges();
            calendarOverlayElement.style.visibility = "hidden";
            calendarOverlayElement.style.opacity = "0";

            let calendarWindow = NativeDesktopCalendar._calendarWindow;

            if (calendarWindow != null) {
              calendarWindow["beforeClose"] = this.onHideOverlay.bind(this);
              requestAnimationFrame(
                this.adjustWindow.bind(this, calendarWindow, calendarOverlayElement, calendar),
              );
            } else {
              requestAnimationFrame(
                this.adjustWindow.bind(this, calendarWindow, calendarOverlayElement, calendar),
              );
            }
          }).bind(this),
        );
      }.bind(this),
    );
  }

  private adjustWindow(
    calendarWindow: NativeDesktopWindow,
    calendarOverlayElement: HTMLDivElement,
    calendar: BfxCalendarComponent,
  ): void {
    let rect: any = calendarOverlayElement.getBoundingClientRect();

    if (rect == null || rect.width == 0 || rect.height == 0) {
      rect = this._originalBounds;
    } else {
      this._originalBounds = rect;
    }

    // Last resort, set fixed bounds
    if (rect == null) {
      rect = { width: 300, height: 280 };
    }

    let width: number = rect.width * NativeDesktopSupport.zoomLevel;
    let height: number = rect.height * NativeDesktopSupport.zoomLevel;

    let parentWindow = NativeDesktopSupport.getWindow(calendar.el.nativeElement);

    calendarWindow.getNativeWindow().document.body.appendChild(calendarOverlayElement);

    let inputControlEl: Element = calendar.autoCompletePosition?.el?.nativeElement;
    if (!calendar.alignToAutocompleteInput) {
      inputControlEl = calendar.inputfieldViewChild.nativeElement;
    }

    if (inputControlEl == null && calendar.inputfieldViewChild != null) {
      inputControlEl = calendar.inputfieldViewChild.nativeElement;
      if (inputControlEl != null) {
        inputControlEl = inputControlEl.parentElement.parentElement;
      }
    }

    let bounds: DOMRect = inputControlEl.getBoundingClientRect();

    let x: number =
      parentWindow.getNativeWindow().screenX +
      (bounds.left - width / NativeDesktopSupport.zoomLevel + bounds.width) *
        NativeDesktopSupport.zoomLevel;

    if (x < this._screenBounds.left) {
      x = parentWindow.getNativeWindow().screenX + bounds.left * NativeDesktopSupport.zoomLevel;
    }

    let y: number = 0;

    if (
      parentWindow.getNativeWindow().screenY +
        (bounds.bottom + height / NativeDesktopSupport.zoomLevel) * NativeDesktopSupport.zoomLevel >
      this._screenBounds.bottom
    ) {
      y =
        parentWindow.getNativeWindow().screenY +
        (bounds.top - height / NativeDesktopSupport.zoomLevel) * NativeDesktopSupport.zoomLevel;
    } else {
      y = parentWindow.getNativeWindow().screenY + bounds.bottom * NativeDesktopSupport.zoomLevel;
    }

    calendarWindow.getNativeWindow().document.body.style.overflow = "hidden";
    calendarWindow.setBounds(x, y, width, height).then(() => {
      calendarWindow.getNativeWindow().document.body.appendChild(calendarOverlayElement);
      calendarOverlayElement.style.visibility = "visible";
      calendarOverlayElement.style.opacity = "1";
      calendarOverlayElement.style.display = "block";
      calendarWindow.show().then(() => {
        calendar.overlayShown = calendar.overlayVisible = true;
        calendar.cd.detectChanges();
        calendar.bindDocumentClickListener();
      });
    });
  }

  public onHideOverlay(): void {
    let calendar = NativeDesktopCalendar._activeComponent;
    this.outputExtendedLogging(
      `DESKTOP: CALENDAR.onHideOverlay(): HIDE => target: '${
        calendar != null ? calendar.target : "NULL"
      }' = calendarWindow: '${NativeDesktopCalendar._calendarWindow?.getId()}' `,
    );

    if (NativeDesktopCalendar._activeComponent != null) {
      calendar.overlayShown = calendar.overlayVisible = false;
      calendar.overlayViewChild.nativeElement.style.display = "none";
      calendar.el.nativeElement.appendChild(calendar.overlayViewChild.nativeElement);
      calendar.overlayViewChild.nativeElement["style"].opacity = "0";
      calendar.overlayViewChild.nativeElement["style"].visibility = "hidden";
      NativeDesktopCalendar._activeComponent = null;
    }

    if (NativeDesktopCalendar._calendarWindow != null) {
      NativeDesktopCalendar._calendarWindow.hide();
    }

    if (NativeDesktopCalendar._calendarDropdownOpen) {
      NativeDesktopCalendar._calendarDropdownOpen = false;
    }
  }

  public static initWindow() {
    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      if (NativeDesktopCalendar._calendarWindow != null) {
        return Promise.resolve();
      }

      if (this._initWindowPromise == null) {
        this._initWindowPromise = new Promise<void>((res) =>
          NativeDesktopSupport.openWindow(
            "calendar",
            WindowType.Utility,
            false,
            null,
            (calendarWindow: NativeDesktopWindow) => {
              NativeDesktopCalendar._calendarWindow = calendarWindow;
              NativeDesktopCalendar._calendarWindow["beforeClose"] = (reasonWhy: any) => {
                return false;
              };
              res();
            },
          ),
        );
      }

      return this._initWindowPromise;
    }

    return Promise.resolve();
  }

  @Input("nativeDesktopActiveWindow")
  public set nativeDesktopActiveWindow(value) {
    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      this.outputExtendedLogging(`DESKTOP: enabling parent small window mode for ${value}`);
      this._activeWindow = true;
    }
  }

  private outputExtendedLogging(value: string, dump: any = null): void {
    this.oeLogger.outputExtendedLogging(value, dump);
  }
}
